import { linkService } from './linkService';

class BatchLinkService {
    constructor() {
        this.abortController = null;
    }

    /**
     * Process a single store URL
     * @param {string} url - The store URL to process
     * @returns {Promise<object>} Processing result for the store
     */
    async processSingleLink(url, signal) {
        try {
            const result = await linkService.processLink(url, signal);
            return {
                storeUrl: result.processedUrl || url,
                success: result.success,
                freeProducts: result.freeProducts || [],
                allProducts: result.allProducts || [],
                isShopifyStore: result.isShopifyStore,
                error: result.message
            };
        } catch (error) {
            if (error.name === 'AbortError') {
                throw error;
            }
            return {
                storeUrl: url,
                success: false,
                freeProducts: [],
                allProducts: [],
                isShopifyStore: false,
                error: error.message
            };
        }
    }

    /**
     * Process a batch of URLs using concurrent requests
     * @param {string[]} urls - Array of URLs to process
     * @param {object} stats - Statistics object to update
     * @param {object} options - Processing options
     * @returns {Promise<void>}
     */
    async processBatchWithConcurrency(urls, stats, options) {
        const { isPausedRef, isStoppedRef, setUploadProgress, setCurrentStore, setProcessingState } = options;
        const results = [];
        const totalUrls = urls.length;
        const processedUrls = { count: 0 };
        
        // Get concurrent requests setting
        const storedConcurrentRequests = localStorage.getItem('concurrentRequests');
        const CONCURRENT_REQUESTS = storedConcurrentRequests ? parseInt(storedConcurrentRequests) : 100;
        
        // Create a new AbortController
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        // Create a queue to track processing order
        let processingQueue = [];
        let completedUrls = new Set();
        let activeRequests = new Set();
        let currentBatchStartIndex = 0;
        let currentBatchSize = 0;

        const processUrl = async (url, index) => {
            if (!url || (isStoppedRef.current && index >= currentBatchStartIndex + currentBatchSize)) return null;
            
            try {
                // Add to processing queue and active requests
                processingQueue.push({ url, index });
                activeRequests.add(index);
                
                const result = await this.processUrlWithFallback(url, () => {
                    // Only update current store if this is the next URL in sequence
                    if (completedUrls.size === index) {
                        const urlObject = new URL(url.startsWith('http') ? url : `https://${url}`);
                        setCurrentStore(urlObject.hostname);
                    }
                }, signal);
                
                this.updateStats(result, stats);
                
                // Mark URL as completed and remove from active requests
                completedUrls.add(index);
                activeRequests.delete(index);
                
                // Update progress based on whether we're finishing or not
                processedUrls.count++;

                if (isStoppedRef.current) {
                    // Calculate progress within current batch
                    const completedInBatch = processedUrls.count - currentBatchStartIndex;
                    const progress = Math.min((completedInBatch / currentBatchSize) * 100, 100);
                    setUploadProgress(progress);
                } else {
                    // Normal progress calculation
                    const progress = Math.min((processedUrls.count / totalUrls) * 100, 100);
                    setUploadProgress(progress);
                }

                // Update current store display to show the latest completed URL in sequence
                while (processingQueue.length > 0 && completedUrls.has(processingQueue[0].index)) {
                    const { url: nextUrl } = processingQueue.shift();
                    const urlObject = new URL(nextUrl.startsWith('http') ? nextUrl : `https://${nextUrl}`);
                    setCurrentStore(urlObject.hostname);
                }
                
                if (result.success && result.isShopifyStore && result.freeProducts?.length > 0) {
                    return {
                        storeUrl: result.storeUrl,
                        freeProducts: result.freeProducts,
                        allProducts: result.allProducts || [],
                        totalFreeProducts: result.freeProducts.length
                    };
                }
            } catch (error) {
                if (error.name === 'AbortError' || error.message === 'AbortError') {
                    throw error; // Re-throw abort errors to handle batch cancellation
                }
                console.error(`Error processing ${url}:`, error);
                // Still mark as completed and remove from active requests
                completedUrls.add(index);
                activeRequests.delete(index);
            }
            return null;
        };

        try {
            // Process URLs in batches
            for (let i = 0; i < urls.length; i += CONCURRENT_REQUESTS) {
                currentBatchStartIndex = i;
                currentBatchSize = Math.min(CONCURRENT_REQUESTS, urls.length - i);

                // Check for pause between each group
                while (isPausedRef.current && !isStoppedRef.current) {
                    // Only set to idle if we're at a batch boundary
                    if (processedUrls.count % CONCURRENT_REQUESTS === 0) {
                        setProcessingState('idle');
                    }
                    await new Promise(resolve => setTimeout(resolve, 500));
                    continue;
                }

                // If we were paused and are now resuming, ensure we're in processing state
                if (!isPausedRef.current) {
                    setProcessingState('processing');
                }

                // Check if processing should be stopped
                if (isStoppedRef.current) {
                    // If we're at a batch boundary (either paused or not) or if we're paused
                    if ((processedUrls.count > 0 && processedUrls.count % CONCURRENT_REQUESTS === 0) || isPausedRef.current) {
                        setProcessingState('idle');
                        setUploadProgress(100);
                        break;
                    }
                    setProcessingState('finishing');
                    // Only process this batch and then break
                    const urlBatch = urls.slice(i, i + CONCURRENT_REQUESTS);
                    const batchPromises = urlBatch.map((url, batchIndex) => 
                        processUrl(url, i + batchIndex)
                    );
                    const batchResults = await Promise.all(batchPromises);
                    results.push(...batchResults.filter(result => result !== null));
                    break;
                }

                // Get the next batch of URLs
                const urlBatch = urls.slice(i, i + CONCURRENT_REQUESTS);

                // Reset queue for new batch
                processingQueue = [];
                completedUrls.clear();

                // Process the batch concurrently
                const batchPromises = urlBatch.map((url, batchIndex) => 
                    processUrl(url, i + batchIndex)
                );

                try {
                    const batchResults = await Promise.all(batchPromises);
                    results.push(...batchResults.filter(result => result !== null));

                    // If we're paused and just finished a batch, set to idle
                    if (isPausedRef.current) {
                        setProcessingState('idle');
                    }
                } catch (error) {
                    if (error.name === 'AbortError' || error.message === 'AbortError') {
                        break;
                    }
                    console.error('Batch error:', error);
                }

                // Add a small delay between batches to avoid overwhelming the system
                if (i + CONCURRENT_REQUESTS < urls.length && !isStoppedRef.current) {
                    await new Promise(resolve => setTimeout(resolve, 50));
                }
            }
        } finally {
            setProcessingState('idle');
        }

        return results;
    }

    /**
     * Process a URL with a fallback attempt
     * @param {string} url - URL to process
     * @param {function} setCurrentStore - Function to update current store
     * @param {AbortSignal} signal - AbortController signal
     * @returns {Promise<object>}
     */
    async processUrlWithFallback(url, setCurrentStore, signal) {
        try {
            const urlObject = new URL(url.startsWith('http') ? url : `https://${url}`);
            setCurrentStore(urlObject.hostname);

            // Get timeout setting
            const storedTimeout = localStorage.getItem('requestTimeout');
            const timeout = (storedTimeout ? parseInt(storedTimeout) : 15) * 1000; // Convert to milliseconds

            // First attempt
            const result = await Promise.race([
                this.processSingleLink(url, signal),
                new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('First attempt timeout')), timeout)
                )
            ]);

            return result;
        } catch (error) {
            if (error.name === 'AbortError') {
                throw error; // Re-throw abort errors
            }
            
            // Get retry attempts setting
            const storedRetries = localStorage.getItem('retryAttempts');
            const maxRetries = storedRetries ? parseInt(storedRetries) : 2;
            let lastError = error;

            // Try remaining retries
            for (let i = 1; i < maxRetries; i++) {
                try {
                    console.log(`Retry attempt ${i} for ${url}`);
                    const storedTimeout = localStorage.getItem('requestTimeout');
                    const timeout = (storedTimeout ? parseInt(storedTimeout) : 15) * 1000;

                    const result = await Promise.race([
                        this.processSingleLink(url, signal),
                        new Promise((_, reject) => 
                            setTimeout(() => reject(new Error(`Attempt ${i + 1} timeout`)), timeout)
                        )
                    ]);

                    return result;
                } catch (retryError) {
                    if (retryError.name === 'AbortError') {
                        throw retryError;
                    }
                    console.error(`Attempt ${i + 1} failed for ${url}:`, retryError.message);
                    lastError = retryError;
                }

                // Add a small delay between retries
                if (i < maxRetries - 1) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }

            console.error(`All ${maxRetries} attempts failed for ${url}`);
            return {
                storeUrl: url,
                success: false,
                freeProducts: [],
                isShopifyStore: false,
                error: `Failed after ${maxRetries} attempts: ${lastError.message}`
            };
        }
    }

    /**
     * Update statistics based on processing result
     * @param {object} result - Processing result
     * @param {object} stats - Statistics object to update
     */
    updateStats(result, stats) {
        stats.processedCount++;
        if (result.success && result.isShopifyStore) {
            stats.validStoresCount++;
            if (result.freeProducts?.length > 0) {
                stats.storesWithFreeProducts++;
            }
        }
    }

    /**
     * Processes a batch of links to find free products
     * @param {string} fileContent - Content of the file with one URL per line
     * @param {object} options - Processing options including refs and setters
     * @returns {Promise<object>} Processing results
     */
    async processBatchLinks(fileContent, options) {
        // Split the content by newlines and remove empty lines
        const links = fileContent
            .split('\n')
            .map(link => link.trim())
            .filter(link => link.length > 0);

        const stats = {
            processedCount: 0,
            validStoresCount: 0,
            storesWithFreeProducts: 0
        };

        const results = await this.processBatchWithConcurrency(links, stats, options);

        return {
            success: true,
            totalLinksProcessed: stats.processedCount,
            validStoresFound: stats.validStoresCount,
            storesWithFreeProducts: stats.storesWithFreeProducts,
            results: results.sort((a, b) => b.totalFreeProducts - a.totalFreeProducts)
        };
    }
}

// Create and export a singleton instance
export const batchLinkService = new BatchLinkService(); 